import { useEffect, useRef, useState } from "react";
import { createEntitlementProjectionRepository } from "./entitlementProjectionRepository";

export const useEntitlementProjectionRepository = ({
  client,
  authToken,
  enabled,
  orgId,
  entitlementRepositoryFactory,
  loadProjection,
  getCachedProjection,
  saveCachedProjection,
  onProjectionState,
  onError,
  repositoryRef,
}) => {
  const repositoryFactory =
    entitlementRepositoryFactory || createEntitlementProjectionRepository;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onProjectionStateRef = useRef(onProjectionState);
  onProjectionStateRef.current = onProjectionState;
  const [owner, setOwner] = useState(null);

  useEffect(() => {
    if (!authToken || !enabled) {
      onProjectionStateRef.current?.();
      setOwner(null);
      repositoryRef.current = null;
      return undefined;
    }
    if (!client) {
      setOwner(null);
      repositoryRef.current = null;
      return undefined;
    }
    let ownerActive = true;
    const reportError = (...args) => onErrorRef.current?.(...args);
    const repository = repositoryFactory({
      token: authToken,
      orgId,
      loadProjection,
      getCachedProjection,
      saveCachedProjection,
      onChange: (state) => {
        if (ownerActive) onProjectionStateRef.current?.(state);
      },
      onError: reportError,
    });
    const onEntitlementUpdate = (event) => repository.apply(event);
    client.subscribeEntitlement(onEntitlementUpdate);
    repository.refresh("initial");
    repositoryRef.current = repository;
    setOwner({ token: authToken, repository });
    return () => {
      ownerActive = false;
      client.unsubscribeEntitlement(onEntitlementUpdate);
      repository.cleanup();
      repositoryRef.current = null;
    };
  }, [
    client,
    authToken,
    enabled,
    orgId,
    repositoryFactory,
    loadProjection,
    getCachedProjection,
    saveCachedProjection,
  ]);

  return owner;
};

export default useEntitlementProjectionRepository;
